// @ts-nocheck
/**
 * flattenTemplateClasses.ts — DEC-066
 *
 * Expands Template Class packages onto elements at load/normalize time:
 *   - Merge class categoryIds into element.categoryIds
 *   - Compose class event/api bindings with element before/after/replace modes
 *   - Merge referenced class library scripts/expressions into document.scripts
 *
 * Pure TypeScript — no framework dependencies.
 */

import type {
  AnyScriptDefinition,
  ElementDefinition,
  ElementScriptBindings,
  EventHandler,
  ExpressionDefinition,
  ScriptEventBinding,
  ScriptEventName,
  TemplateClassDefinition,
  TemplateDocument,
  TemplateScriptLibraryInline,
} from '../../types/canonical';
import { normalizeEventHandler } from '../../types/eventHandler';
import { classBindingRoleKey, isClassScriptEventName } from '../../types/templateClass';

/** Resolve ancestor chain root-first (parent before child). Detects cycles. */
export function resolveClassAncestry(
  classId: string,
  classById: ReadonlyMap<string, TemplateClassDefinition>,
): TemplateClassDefinition[] | null {
  const chain: TemplateClassDefinition[] = [];
  const seen = new Set<string>();
  let currentId: string | undefined = classId;

  while (currentId) {
    if (seen.has(currentId)) {
      return null;
    }
    seen.add(currentId);
    const def = classById.get(currentId);
    if (!def) {
      return null;
    }
    chain.push(def);
    currentId = def.extendsClassId;
  }

  chain.reverse();
  return chain;
}

function scriptIdFromHandler(handler: EventHandler): string | null {
  const normalized = normalizeEventHandler(handler);
  if (!normalized || normalized.kind !== 'script') {
    return null;
  }
  return normalized.scriptId;
}

/**
 * Merge class event bindings with overwrite-by-bindingRoleId (fallback id).
 * Later classes in `ordered` replace earlier bindings that share the same role.
 */
export function mergeClassEventBindings(
  orderedClasses: readonly TemplateClassDefinition[],
): ScriptEventBinding[] {
  const byRole = new Map<string, ScriptEventBinding>();
  const order: string[] = [];

  for (const cls of orderedClasses) {
    for (const binding of cls.bindings?.events ?? []) {
      if (!isClassScriptEventName(binding.event)) {
        continue;
      }
      const role = classBindingRoleKey(binding);
      const stamped: ScriptEventBinding = {
        ...binding,
        bindingRoleId: role,
        sourceClassId: cls.id,
      };
      if (!byRole.has(role)) {
        order.push(role);
      }
      byRole.set(role, stamped);
    }
  }

  return order.map((role) => byRole.get(role)!);
}

/**
 * Merge class API handlers with overwrite-by-scriptId.
 * Later classes replace earlier entries that expose the same scriptId.
 */
export function mergeClassApiHandlers(
  orderedClasses: readonly TemplateClassDefinition[],
): EventHandler[] {
  const byScriptId = new Map<string, EventHandler>();
  const order: string[] = [];

  for (const cls of orderedClasses) {
    for (const handler of cls.bindings?.api ?? []) {
      const scriptId = scriptIdFromHandler(handler);
      if (!scriptId) {
        continue;
      }
      const stamped = normalizeEventHandler(handler) ?? handler;
      if (!byScriptId.has(scriptId)) {
        order.push(scriptId);
      }
      byScriptId.set(scriptId, stamped);
    }
  }

  return order.map((id) => byScriptId.get(id)!);
}

/**
 * Compose class event bindings with element bindings using classComposition.
 * Per event: before → remaining class roles → after/replace handlers.
 */
export function composeEventBindings(
  classEvents: readonly ScriptEventBinding[],
  elementEvents: readonly ScriptEventBinding[],
): ScriptEventBinding[] {
  const classByEvent = new Map<ScriptEventName, ScriptEventBinding[]>();
  for (const binding of classEvents) {
    const list = classByEvent.get(binding.event) ?? [];
    list.push(binding);
    classByEvent.set(binding.event, list);
  }

  const elementByEvent = new Map<ScriptEventName, ScriptEventBinding[]>();
  const eventOrder: ScriptEventName[] = [];
  const seenEvent = new Set<ScriptEventName>();

  for (const binding of classEvents) {
    if (!seenEvent.has(binding.event)) {
      seenEvent.add(binding.event);
      eventOrder.push(binding.event);
    }
  }
  for (const binding of elementEvents) {
    const list = elementByEvent.get(binding.event) ?? [];
    list.push(binding);
    elementByEvent.set(binding.event, list);
    if (!seenEvent.has(binding.event)) {
      seenEvent.add(binding.event);
      eventOrder.push(binding.event);
    }
  }

  const out: ScriptEventBinding[] = [];

  for (const event of eventOrder) {
    const classList = [...(classByEvent.get(event) ?? [])];
    const elList = elementByEvent.get(event) ?? [];

    const before: ScriptEventBinding[] = [];
    const after: ScriptEventBinding[] = [];
    const replace: ScriptEventBinding[] = [];
    const rolesToOmit = new Set<string>();
    let replaceAll = false;

    for (const binding of elList) {
      const mode = binding.classComposition ?? 'after';
      if (mode === 'before') {
        before.push(binding);
      } else if (mode === 'replace') {
        replace.push(binding);
        const roles = binding.replaceRoleIds ?? [];
        if (roles.length === 0) {
          replaceAll = true;
        } else {
          for (const role of roles) {
            rolesToOmit.add(role);
          }
        }
      } else {
        after.push(binding);
      }
    }

    const remainingClass = replaceAll
      ? []
      : classList.filter((b) => !rolesToOmit.has(classBindingRoleKey(b)));

    out.push(...before, ...remainingClass, ...replace, ...after);
  }

  return out;
}

function mergeUniqueIds(base: readonly string[], extra: readonly string[]): string[] {
  const seen = new Set(base);
  const out = [...base];
  for (const id of extra) {
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

function collectReferencedScriptIds(
  events: readonly ScriptEventBinding[],
  api: readonly EventHandler[],
): Set<string> {
  const ids = new Set<string>();
  for (const binding of events) {
    const normalized = normalizeEventHandler(binding.handler);
    if (normalized?.kind === 'script') {
      ids.add(normalized.scriptId);
    }
  }
  for (const handler of api) {
    const scriptId = scriptIdFromHandler(handler);
    if (scriptId) {
      ids.add(scriptId);
    }
  }
  return ids;
}

/** True when a library script has runnable Blockly code safe to hoist into document.scripts. */
function hasHoistableScriptCode(script: AnyScriptDefinition): boolean {
  return typeof script.code === 'string' && script.code.trim() !== '';
}

function collectLibraryMembers(
  classes: readonly TemplateClassDefinition[],
  referencedScriptIds: ReadonlySet<string>,
): { scripts: AnyScriptDefinition[]; expressions: ExpressionDefinition[] } {
  const scripts: AnyScriptDefinition[] = [];
  const expressions: ExpressionDefinition[] = [];
  const seenScript = new Set<string>();
  const seenExpr = new Set<string>();

  // Scripts: only those referenced by class/element bindings (tree-shake unused).
  // Skip empty-code stubs: document.scripts structural validation requires non-empty
  // Blockly code, and the pipeline re-runs loadTemplate on already-flattened registry
  // docs — hoisting an empty stub blanks every layout ("template failed to load or validate").
  for (const cls of classes) {
    for (const script of cls.library?.scripts ?? []) {
      if (!referencedScriptIds.has(script.id) || seenScript.has(script.id)) {
        continue;
      }
      if (!hasHoistableScriptCode(script)) {
        continue;
      }
      seenScript.add(script.id);
      scripts.push(script);
    }
  }

  // Expressions: all library expressions from every contributing class (layout
  // ValueSources may reference them without class lifecycle bindings).
  for (const cls of classes) {
    for (const expr of cls.library?.expressions ?? []) {
      if (seenExpr.has(expr.id)) {
        continue;
      }
      seenExpr.add(expr.id);
      expressions.push(expr);
    }
  }

  return { scripts, expressions };
}

function mergeLibraries(
  base: TemplateScriptLibraryInline | undefined,
  extraScripts: readonly AnyScriptDefinition[],
  extraExpressions: readonly ExpressionDefinition[],
): TemplateScriptLibraryInline | undefined {
  if (extraScripts.length === 0 && extraExpressions.length === 0) {
    return base;
  }
  const scripts = [...(base?.scripts ?? [])];
  const expressions = [...(base?.expressions ?? [])];
  const seenScript = new Set(scripts.map((s) => s.id));
  const seenExpr = new Set(expressions.map((e) => e.id));

  for (const script of extraScripts) {
    if (!seenScript.has(script.id)) {
      seenScript.add(script.id);
      scripts.push(script);
    }
  }
  for (const expr of extraExpressions) {
    if (!seenExpr.has(expr.id)) {
      seenExpr.add(expr.id);
      expressions.push(expr);
    }
  }

  return {
    scripts,
    ...(expressions.length > 0 ? { expressions } : {}),
  };
}

function flattenElementClasses(
  element: ElementDefinition,
  classById: ReadonlyMap<string, TemplateClassDefinition>,
): {
  element: ElementDefinition;
  contributingClasses: TemplateClassDefinition[];
  classEvents: ScriptEventBinding[];
  classApi: EventHandler[];
} {
  const classIds = element.classIds ?? [];
  if (classIds.length === 0) {
    return { element, contributingClasses: [], classEvents: [], classApi: [] };
  }

  const contributingClasses: TemplateClassDefinition[] = [];
  const seenClass = new Set<string>();
  let mergedCategoryIds = [...(element.categoryIds ?? [])];
  const allClassEvents: ScriptEventBinding[] = [];
  const allClassApi: EventHandler[] = [];

  for (const classId of classIds) {
    const ancestry = resolveClassAncestry(classId, classById);
    if (!ancestry) {
      continue;
    }
    for (const cls of ancestry) {
      if (!seenClass.has(cls.id)) {
        seenClass.add(cls.id);
        contributingClasses.push(cls);
      }
    }
    mergedCategoryIds = mergeUniqueIds(mergedCategoryIds, ancestry.flatMap((c) => c.categoryIds));
    allClassEvents.push(...mergeClassEventBindings(ancestry));
    allClassApi.push(...mergeClassApiHandlers(ancestry));
  }

  // Deduplicate event bindings across multiple attached classes by role (later wins)
  const eventByRole = new Map<string, ScriptEventBinding>();
  const eventOrder: string[] = [];
  for (const binding of allClassEvents) {
    const role = classBindingRoleKey(binding);
    if (!eventByRole.has(role)) {
      eventOrder.push(role);
    }
    eventByRole.set(role, binding);
  }
  const classEvents = eventOrder.map((role) => eventByRole.get(role)!);

  const apiByScript = new Map<string, EventHandler>();
  const apiOrder: string[] = [];
  for (const handler of allClassApi) {
    const scriptId = scriptIdFromHandler(handler);
    if (!scriptId) {
      continue;
    }
    if (!apiByScript.has(scriptId)) {
      apiOrder.push(scriptId);
    }
    apiByScript.set(scriptId, handler);
  }
  const classApi = apiOrder.map((id) => apiByScript.get(id)!);

  // Idempotency: strip previously flattened class event bindings (stamped with
  // sourceClassId) so a second flatten does not treat them as element-owned.
  const elementEvents = (element.scripts?.events ?? []).filter(
    (binding) => !binding.sourceClassId,
  );
  const classApiScriptIds = new Set(
    classApi
      .map((handler) => scriptIdFromHandler(handler))
      .filter((id): id is string => id != null),
  );
  // Idempotency: drop element api entries that match class-exposed scriptIds
  // (those were prepended on a prior flatten pass).
  const elementOwnedApi = (element.scripts?.api ?? []).filter((handler) => {
    const scriptId = scriptIdFromHandler(handler);
    return scriptId == null || !classApiScriptIds.has(scriptId);
  });
  const composedEvents = composeEventBindings(classEvents, elementEvents);

  const scripts: ElementScriptBindings = {
    ...element.scripts,
    events: composedEvents,
    api:
      classApi.length > 0 || elementOwnedApi.length > 0
        ? [...classApi, ...elementOwnedApi]
        : element.scripts?.api,
  };

  const hasScripts =
    (scripts.events?.length ?? 0) > 0 ||
    (scripts.api?.length ?? 0) > 0 ||
    (scripts.actions?.length ?? 0) > 0 ||
    (scripts.computedValues?.length ?? 0) > 0 ||
    (scripts.fieldBindings?.bindings.length ?? 0) > 0;

  return {
    element: {
      ...element,
      categoryIds: mergedCategoryIds,
      scripts: hasScripts ? scripts : element.scripts,
    },
    contributingClasses,
    classEvents,
    classApi,
  };
}

/**
 * Flatten template classes onto elements and merge referenced class library
 * members into `document.scripts`. Does not mutate input.
 *
 * Idempotent: a second pass on an already-flattened document yields the same
 * effective categoryIds, event/api composition, and merged library (safe when
 * DescriptorPipeline re-runs loadTemplate/normalize on a registry document).
 */
export function flattenTemplateClasses(document: TemplateDocument): TemplateDocument {
  const classes = document.classes ?? [];
  if (classes.length === 0) {
    return document;
  }

  const classById = new Map(classes.map((c) => [c.id, c]));
  const allContributing: TemplateClassDefinition[] = [];
  const seenContrib = new Set<string>();
  const referencedScriptIds = new Set<string>();

  const elements = document.elements.map((element) => {
    const { element: next, contributingClasses, classEvents, classApi } = flattenElementClasses(
      element,
      classById,
    );
    for (const cls of contributingClasses) {
      if (!seenContrib.has(cls.id)) {
        seenContrib.add(cls.id);
        allContributing.push(cls);
      }
    }
    for (const id of collectReferencedScriptIds(classEvents, classApi)) {
      referencedScriptIds.add(id);
    }
    // Also keep scripts referenced by element composition wires that target class scripts
    for (const id of collectReferencedScriptIds(next.scripts?.events ?? [], next.scripts?.api ?? [])) {
      referencedScriptIds.add(id);
    }
    return next;
  });

  const { scripts: extraScripts, expressions: extraExpressions } = collectLibraryMembers(
    allContributing,
    referencedScriptIds,
  );

  const mergedScripts = mergeLibraries(document.scripts, extraScripts, extraExpressions);

  // After merge, drop class-library copies of ids now in document.scripts so
  // semantic validation (duplicate_script_id / duplicate_expression_id) passes
  // when DescriptorPipeline re-runs loadTemplate on a registry document.
  const mergedScriptIds = new Set((mergedScripts?.scripts ?? []).map((s) => s.id));
  const mergedExprIds = new Set((mergedScripts?.expressions ?? []).map((e) => e.id));
  const classesWithoutMergedLibrary = classes.map((cls) => {
    if (!cls.library) {
      return cls;
    }
    const scripts = (cls.library.scripts ?? []).filter((s) => !mergedScriptIds.has(s.id));
    const expressions = (cls.library.expressions ?? []).filter((e) => !mergedExprIds.has(e.id));
    if (scripts.length === 0 && expressions.length === 0) {
      return { ...cls, library: undefined };
    }
    if (
      scripts.length === (cls.library.scripts ?? []).length &&
      expressions.length === (cls.library.expressions ?? []).length
    ) {
      return cls;
    }
    return {
      ...cls,
      library: {
        scripts,
        ...(expressions.length > 0 ? { expressions } : {}),
      },
    };
  });

  return {
    ...document,
    classes: classesWithoutMergedLibrary,
    elements,
    ...(mergedScripts ? { scripts: mergedScripts } : {}),
  };
}

/**
 * Tree-shake unused classes and unused class library members for editor export.
 * Keeps classes referenced by any element's classIds (including ancestors).
 */
export function treeShakeTemplateClasses(document: TemplateDocument): TemplateDocument {
  const classes = document.classes ?? [];
  if (classes.length === 0) {
    return document;
  }

  const classById = new Map(classes.map((c) => [c.id, c]));
  const keep = new Set<string>();

  for (const element of document.elements) {
    for (const classId of element.classIds ?? []) {
      const ancestry = resolveClassAncestry(classId, classById);
      if (!ancestry) {
        keep.add(classId);
        continue;
      }
      for (const cls of ancestry) {
        keep.add(cls.id);
      }
    }
  }

  const shaken: TemplateClassDefinition[] = [];
  for (const cls of classes) {
    if (!keep.has(cls.id)) {
      continue;
    }
    const referenced = collectReferencedScriptIds(cls.bindings?.events ?? [], cls.bindings?.api ?? []);
    const scripts = (cls.library?.scripts ?? []).filter((s) => referenced.has(s.id));
    // Keep all class expressions — layout may reference them without bindings.
    const expressions = [...(cls.library?.expressions ?? [])];
    const library: TemplateScriptLibraryInline | undefined =
      scripts.length > 0 || expressions.length > 0
        ? {
            scripts,
            ...(expressions.length > 0 ? { expressions } : {}),
          }
        : undefined;
    shaken.push({
      ...cls,
      ...(library ? { library } : { library: undefined }),
    });
  }

  return {
    ...document,
    classes: shaken.length > 0 ? shaken : undefined,
  };
}
